import * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'
import { TypeChecker } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import type { Collection } from '../../collection'
import { Selection } from '../../models'
import type { Feature } from '../feature'

/** Keeps one canonical browser selection valid as inventory and choices change. */
export const reconcileSelection = store.effects.create(
  (feature: Feature, collection: Collection) =>
    rxjs
      .merge(
        feature.actions.load.success.observe().pipe(
          rxjs.withLatestFrom(
            feature.selectors.selection.asObservable,
            feature.selectors.agents.asObservable,
          ),
          rxjs.map(([, selection, agents]) =>
            _reconcileAgentSelection(selection, agents),
          ),
        ),
        feature.actions.selectMode.observe().pipe(
          rxjs.withLatestFrom(
            feature.selectors.selection.asObservable,
            feature.selectors.agents.asObservable,
          ),
          rxjs.map(([action, selection, agents]) =>
            action.data === selection.mode ||
            (action.data === Selection.Mode.Agent && agents.length === 0)
              ? undefined
              : { ...selection, mode: action.data },
          ),
        ),
        feature.actions.selectAgent.observe().pipe(
          rxjs.withLatestFrom(
            feature.selectors.selection.asObservable,
            feature.selectors.agents.asObservable,
          ),
          rxjs.map(([action, selection, agents]) => {
            const agent = agents.find(
              candidate => candidate.agentId === action.data,
            )
            const dataSource = agent?.dataSources[0]
            return {
              ...selection,
              agent: {
                agentId: agent?.agentId,
                dataSource: _toTarget(dataSource),
              },
            }
          }),
        ),
        feature.actions.selectDataSource.observe().pipe(
          rxjs.withLatestFrom(
            feature.selectors.selection.asObservable,
            feature.selectors.selectedAgent.asObservable,
          ),
          rxjs.map(([action, selection, agent]) => {
            const selectedDataSource = action.data
            const dataSource = selectedDataSource
              ? agent?.dataSources.find(
                  candidate =>
                    candidate.id === selectedDataSource.id &&
                    candidate.knowledgeBaseId ===
                      selectedDataSource.knowledgeBaseId,
                )
              : undefined
            const target = _toTarget(dataSource)
            return _isSameTarget(selection.agent.dataSource, target)
              ? undefined
              : {
                  ...selection,
                  agent: {
                    ...selection.agent,
                    dataSource: target,
                  },
                }
          }),
        ),
        feature.actions.selectTargetType.observe().pipe(
          rxjs.withLatestFrom(feature.selectors.selection.asObservable),
          rxjs.map(([action, selection]) => {
            if (action.data === selection.shared.targetType) {
              return undefined
            }
            return {
              ...selection,
              shared:
                action.data === undefined
                  ? {}
                  : {
                      ...selection.shared,
                      targetType: action.data,
                      zoneId:
                        action.data ===
                        ents.core.agent.KnowledgeBase.Target.Type.Tenant
                          ? undefined
                          : selection.shared.zoneId,
                    },
            }
          }),
        ),
        feature.actions.selectTenant.observe().pipe(
          rxjs.withLatestFrom(
            feature.selectors.selection.asObservable,
            feature.selectors.tenants.asObservable,
          ),
          rxjs.map(([action, selection, tenants]) => {
            const tenantId = tenants.some(tenant => tenant.id === action.data)
              ? action.data
              : undefined
            return tenantId === selection.shared.tenantId
              ? undefined
              : {
                  ...selection,
                  shared: {
                    ...selection.shared,
                    tenantId,
                    zoneId: undefined,
                  },
                }
          }),
        ),
        feature.actions.selectZone.observe().pipe(
          rxjs.withLatestFrom(
            feature.selectors.selection.asObservable,
            feature.selectors.zones.asObservable,
          ),
          rxjs.map(([action, selection, zones]) => {
            const zoneId = zones.some(zone => zone.id === action.data)
              ? action.data
              : undefined
            return zoneId === selection.shared.zoneId
              ? undefined
              : {
                  ...selection,
                  shared: {
                    ...selection.shared,
                    zoneId,
                  },
                }
          }),
        ),
      )
      .pipe(
        rxjs.filter(TypeChecker.isNotNullish.raw),
        rxjs.tap(selection => _writeSelection(collection, selection)),
        rxjs.ignoreElements(),
      ),
)

/** Repairs the inactive Agent branch without changing the selected workspace. */
function _reconcileAgentSelection(
  selection: Selection,
  agents: readonly ents.core.agent.KnowledgeBase.Management.Agent[],
): Selection {
  const agent =
    agents.find(candidate => candidate.agentId === selection.agent.agentId) ??
    agents[0]
  const dataSource =
    agent?.dataSources.find(candidate =>
      _isSameTarget(selection.agent.dataSource, _toTarget(candidate)),
    ) ?? agent?.dataSources[0]

  return {
    ...selection,
    agent: {
      agentId: agent?.agentId,
      dataSource: _toTarget(dataSource),
    },
    mode: agent ? selection.mode : Selection.Mode.Shared,
  }
}

/** Converts an inventory source to the identifiers retained by selection. */
function _toTarget(
  dataSource: ents.core.agent.KnowledgeBase.Management.DataSource | undefined,
): ents.core.agent.KnowledgeBase.Management.DataSource.Target | undefined {
  return dataSource
    ? {
        dataSourceId: dataSource.id,
        knowledgeBaseId: dataSource.knowledgeBaseId,
      }
    : undefined
}

/** Checks whether two retained source identifiers select the same source. */
function _isSameTarget(
  first: ents.core.agent.KnowledgeBase.Management.DataSource.Target | undefined,
  second:
    | ents.core.agent.KnowledgeBase.Management.DataSource.Target
    | undefined,
): boolean {
  return (
    first?.dataSourceId === second?.dataSourceId &&
    first?.knowledgeBaseId === second?.knowledgeBaseId
  )
}

/** Applies the selection as one current-list filter and resets local paging. */
function _writeSelection(collection: Collection, selection: Selection): void {
  const appliedParams = collection.plugins.currentListPlugin.appliedParams
  appliedParams.actions.writeAllProperties
    .from('Admin.KnowledgeBases.Selection.Changed')
    .dispatch({
      values: {
        filters: selection,
        quantity: appliedParams.selectors.quantity.asSignal(),
        skip: 0,
      },
    })
}
